import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "";

function resolveAvatarUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const normalized = url.startsWith("/") ? url : `/${url}`;
  return `${API_BASE}${normalized}`;
}

function toInt(value) {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export default function ConsultantProfile() {
  const [successMsg, setSuccessMsg] = useState("");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const consultantId = Number(localStorage.getItem("consultant_id"));
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    gender: "",
    age: "",
    avatar: "",
    city: "",
    experience_years: "",
    background: "",
    specialties: "",
    consultation_style: "",
    intro: "",
    price: "",
    duration: "",
    service_types: "",
  });

  const validateForm = () => {
    if (!form.name.trim()) {
      alert("请输入姓名");
      return false;
    }
    if (!form.gender) {
      alert("请选择性别");
      return false;
    }
    if (!form.age || Number(form.age) <= 0) {
      alert("请输入正确年龄");
      return false;
    }
    if (!form.city.trim()) {
      alert("请输入城市");
      return false;
    }
    if (!form.price || Number(form.price) <= 0) {
      alert("请输入正确价格");
      return false;
    }
    if (!form.duration || Number(form.duration) <= 0) {
      alert("请输入咨询时长");
      return false;
    }
    return true;
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("consultant_id", String(consultantId));

    setAvatarUploading(true);
    try {
      const res = await fetch(`${API_BASE}/api/upload/avatar`, {
        method: "POST",
        body: formData,
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (res.ok && data?.url) {
        setForm((prev) => ({
          ...prev,
          avatar: data.url,
        }));
        localStorage.setItem("avatar", data.url);
        window.dispatchEvent(new Event("avatarUpdated"));
        setSuccessMsg("头像上传成功");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        alert(data?.detail || data?.msg || "头像上传失败");
      }
    } catch (err) {
      console.error(err);
      alert("头像上传失败，请确认后端已启动");
    } finally {
      setAvatarUploading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/consultant/profile/${consultantId}?owner_consultant_id=${consultantId}`
      );
      const data = await res.json();

      if (res.ok) {
        setForm({
          name: data.name || "",
          gender: data.gender || "",
          age: data.age ?? "",
          avatar: data.avatar || "",
          city: data.city || "",
          experience_years: data.experience_years ?? "",
          background: data.background || "",
          specialties: data.specialties || "",
          consultation_style: data.consultation_style || "",
          intro: data.intro || "",
          price: data.price ?? "",
          duration: data.duration ?? "",
          service_types: data.service_types || "",
        });
      } else {
        alert(data.detail || "获取资料失败");
      }
    } catch (err) {
      console.error(err);
      alert("服务器错误");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!consultantId) {
      alert("请先登录");
      navigate("/login");
      return;
    }
    fetchProfile();
  }, [consultantId, navigate]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        consultant_id: consultantId,
        name: form.name.trim(),
        gender: form.gender,
        age: toInt(form.age),
        city: form.city.trim(),
        avatar: form.avatar || null,
        experience_years: toInt(form.experience_years),
        background: form.background || null,
        specialties: form.specialties || null,
        consultation_style: form.consultation_style || null,
        intro: form.intro || null,
        price: toInt(form.price),
        duration: toInt(form.duration),
        service_types: form.service_types || null,
      };

      const res = await fetch(`${API_BASE}/api/consultant/profile/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccessMsg(data.msg || data.detail || "保存成功");
        if (data.consultant?.avatar || form.avatar) {
          localStorage.setItem("avatar", data.consultant?.avatar || form.avatar);
          window.dispatchEvent(new Event("avatarUpdated"));
        }
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        alert(data.detail || "保存失败");
      }
    } catch (err) {
      console.error(err);
      alert("服务器错误");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-[320px] flex items-center justify-center text-xl text-gray-400">
        加载中...
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <h1 className="text-3xl font-bold text-center">编辑咨询师资料</h1>

      <div className="space-y-10">
        <div>
          <h2 className="text-xl font-bold mb-4">基础信息</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="姓名"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="border p-3 rounded-lg"
            />

            <input
              type="number"
              min={1}
              max={100}
              placeholder="年龄"
              value={form.age}
              onChange={(e) => handleChange("age", e.target.value)}
              className="border p-3 rounded-lg"
            />

            <select
              value={form.gender}
              onChange={(e) => handleChange("gender", e.target.value)}
              className="border p-3 rounded-lg"
            >
              <option value="">选择性别</option>
              <option value="男">男</option>
              <option value="女">女</option>
            </select>

            <input
              type="text"
              placeholder="城市"
              value={form.city}
              onChange={(e) => handleChange("city", e.target.value)}
              className="border p-3 rounded-lg"
            />
          </div>

          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <button
              type="button"
              disabled={avatarUploading}
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-60"
            >
              {avatarUploading ? "上传中..." : "上传头像"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleAvatarUpload(file);
                e.target.value = "";
              }}
            />
            <span className="text-sm text-gray-400">
              上传成功后自动保存，修改其他资料后请点「保存资料」
            </span>
          </div>

          {form.avatar ? (
            <img
              src={resolveAvatarUrl(form.avatar)}
              alt="头像"
              className="w-28 h-28 rounded-full object-cover mt-4 border"
            />
          ) : null}
        </div>

        <div>
          <h2 className="text-xl font-bold mb-4">专业信息</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="number"
              min={0}
              placeholder="从业年限"
              value={form.experience_years}
              onChange={(e) =>
                handleChange("experience_years", e.target.value)
              }
              className="border p-3 rounded-lg"
            />

            <input
              type="text"
              placeholder="专业背景"
              value={form.background}
              onChange={(e) => handleChange("background", e.target.value)}
              className="border p-3 rounded-lg"
            />
          </div>

          <input
            type="text"
            placeholder="擅长领域（逗号分隔）"
            value={form.specialties}
            onChange={(e) => handleChange("specialties", e.target.value)}
            className="border p-3 rounded-lg w-full mt-4"
          />

          <input
            type="text"
            placeholder="咨询风格"
            value={form.consultation_style}
            onChange={(e) =>
              handleChange("consultation_style", e.target.value)
            }
            className="border p-3 rounded-lg w-full mt-4"
          />

          <textarea
            rows="5"
            placeholder="个人介绍"
            value={form.intro}
            onChange={(e) => handleChange("intro", e.target.value)}
            className="border p-3 rounded-lg w-full mt-4"
          />
        </div>

        <div>
          <h2 className="text-xl font-bold mb-4">服务设置</h2>

          <div className="grid md:grid-cols-3 gap-4">
            <input
              min="1"
              type="number"
              placeholder="价格"
              value={form.price}
              onChange={(e) => handleChange("price", e.target.value)}
              className="border p-3 rounded-lg"
            />

            <input
              type="number"
              min="1"
              placeholder="时长（分钟）"
              value={form.duration}
              onChange={(e) => handleChange("duration", e.target.value)}
              className="border p-3 rounded-lg"
            />

            <input
              type="text"
              placeholder="服务方式 text,voice,video"
              value={form.service_types}
              onChange={(e) => handleChange("service_types", e.target.value)}
              className="border p-3 rounded-lg"
            />
          </div>
        </div>

        {successMsg ? (
          <div className="bg-green-100 text-green-700 border border-green-300 px-4 py-3 rounded-lg">
            {successMsg}
          </div>
        ) : null}

        <button
          type="button"
          onClick={handleSave}
          disabled={loading || avatarUploading}
          className="w-full bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white py-3 rounded-lg text-lg"
        >
          {loading ? "保存中..." : "保存资料"}
        </button>
      </div>
    </div>
  );
}
